<template>
  <div class="ProductsTable">
    <vue-slideout-panel
      v-model="showInfoDialog"
      :widths="['30%']"
      close-html="<i class='fal fa-lg fa-times'></i>"
      @close="showInfoDialog = false"
    >
      <product-info
        v-if="selectedProduct"
        :product-object="selectedProduct"
        @added-to-cart="addToCart($event);showInfoDialog = false"
        @close="showInfoDialog = false"
        @click:history="showPurchases($event)"
      />
    </vue-slideout-panel>

    <v-text-field
      v-if="includeSearch"
      ref="input"
      v-model="search"
      :label="$t('Search')"
      autocomplete="off"
      autofocus
      class="mb-6"
      clearable
      filled
      hide-details
      prepend-inner-icon="fal fa-search"
      single-line
      @input="fuzzyDebounced"
    />

    <v-data-table
      :headers="headers"
      :items="filteredProducts"
      :loading="$wait.is('fetching products')"
      v-bind="dataTableProps"
    >
      <template v-slot:item="props">
        <tr
          style="cursor: pointer"
          @click="showInfo(props.item)"
        >
          <td>
            <template v-if="$store.getters.locale === $store.state.secondaryLocale">{{ props.item.nameTranslated }}</template>
            <template v-else>{{ props.item.name }}</template>
          </td>
          <td>
            <stock-chip
              v-if="itemIsProduct(props.item)"
              :value="_.get(props, 'item.storeStock.stock', 0)"
              :loading="$wait.is('fetching product stocks')"
            />
            <template v-else>-</template>
          </td>
          <td>
            <template v-if="itemIsProduct(props.item)">{{ props.item.barcodes }}</template>
            <template v-else>-</template>
          </td>
          <td>{{ _.get(props, 'item.brand.name', '-') }}</td>
          <td>
            <template v-if="itemIsProduct(props.item)">{{ props.item.retailPromo ? props.item.retailPromo : props.item.retailPrice | money }}</template>
            <template v-else>-</template>
          </td>
          <td>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  class="ma-0"
                  icon
                  small
                  v-on="on"
                  @click.stop="showPurchases(props.item)"
                >
                  <v-icon small>fal fa-calendar-alt</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Sales History') }}</span>
            </v-tooltip>

            <v-tooltip
              v-if="itemIsProduct(props.item)"
              top
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-show="cashRegister.openedReport"
                  :loading="$wait.is(`${props.item.id} adding to cart`)"
                  icon
                  small
                  text
                  v-on="on"
                  @click.stop="addToCart(props.item)"
                >
                  <v-icon
                    v-if="returnType"
                    small
                  >far fa-undo-alt</v-icon>
                  <v-icon
                    v-else
                    small
                  >far fa-shopping-basket</v-icon>
                </v-btn>
              </template>
              <span v-if="!returnType">{{ $t('Add to {0}', [$t('Cart').toLowerCase()]) }}</span>
              <span v-else>{{ $t(returnType.label) }}</span>
            </v-tooltip>

            <v-tooltip
              v-if="itemIsService(props.item)"
              top
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-show="cashRegister.openedReport"
                  :loading="$wait.is(`${props.item.id} adding to cart`)"
                  icon
                  small
                  text
                  v-on="on"
                  @click.stop="openServiceProductDialog(props.item)"
                >
                  <v-icon small>far fa-shopping-basket</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('Add to {0}', [$t('Cart').toLowerCase()]) }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showProductReceiptsDialog"
      eager
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card
        v-if="selectedProduct"
        tile
      >
        <v-app-bar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>
            <v-icon class="mr-2">
              fal fa-inventory
            </v-icon>
            {{ $t('Sales History') }}: {{ selectedProduct.name }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn
              dark
              icon
              @click.native="showProductReceiptsDialog = false"
            >
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-app-bar>
        <v-container
          fluid
          grid-list-md
        >
          <receipts-table
            v-if="selectedProduct && showProductReceiptsDialog"
            :include-speed-dial="false"
            :product-object="selectedProduct"
            include-search
          />
        </v-container>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showServiceProductDialog"
      eager
      max-width="450"
    >
      <forms-service-product-add
        ref="form-note"
        :form-title="$t('Service')"
        :product-object="selectedProduct"
        @saved="saveServiceProductDialog($event)"
      />
    </v-dialog>

    <select-tax-stamp-dialog
      v-if="showTaxStampDialog"
      :show-dialog="showTaxStampDialog"
      :item="selectedProduct"
      @item-selected="selectTaxStamp($event)"
      @closed="showTaxStampDialog = false" />

    <barcode-scanner
      v-if="initBarcodeScanner"
      @scan-init="scanCode"
    />
  </div>
</template>

<script>
import BarcodeScanner from '@/components/BarcodeScanner'
import StockChip from '@/components/chips/StockChip'
import ProductInfo from '@/components/products/ProductInfo'
import ReceiptsTable from '@/components/receipts/ReceiptsTable'
import SelectTaxStampDialog from '@/components/dialogs/SelectTaxStampDialog'
import FormsServiceProductAdd from '@/components/forms/notes/FormsServiceProductAdd'
import productService from '@/services/products'
import { VDataTableDefaultProps as dataTableProps } from '@/utils/default-props/v-data-table'
import { fuzzyfilterProducts } from '@/utils/functions'
import focusInputMixin from '@/utils/mixins/focusInputMixin'
import productTypeMixin from '@/utils/mixins/productTypeMixin'
import camelcaseKeys from 'camelcase-keys'
import VueSlideoutPanel from 'vue-slideout-panel'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'ProductsTable',

  components: {
    ProductInfo,
    VueSlideoutPanel,
    BarcodeScanner,
    StockChip,
    ReceiptsTable,
    FormsServiceProductAdd,
    SelectTaxStampDialog
  },

  mixins: [ focusInputMixin, productTypeMixin ],

  props: {
    includeSearch: {
      default: false,
      type: Boolean
    },
    tag: {
      default: null,
      required: false,
      type: String
    },
    returnType: {
      default: null,
      required: false,
      type: Object
    },
    initBarcodeScanner: {
      default: true,
      required: false,
      type: Boolean
    }
  },

  data () {
    return {
      headers: [
        {
          text: this.$t('Name'),
          value: 'name',
          width: '30%'
        },
        {
          text: this.$t('Stock'),
          value: 'storeStock.stock'
        },
        {
          text: this.$t('Barcode'),
          value: 'barcode'
        },
        {
          text: this.$t('Brand'),
          value: 'brand.name'
        },
        {
          text: this.$t('Price'),
          value: 'retailPrice'
        },
        {
          text: '',
          value: 'id',
          sortable: false,
          width: '120px'
        }
      ],
      search: '',
      scores: {},
      products: this.$store.state.products,
      filteredProducts: this.$store.state.products,
      showInfoDialog: false,
      showProductReceiptsDialog: false,
      selectedProduct: null,
      dataTableProps,
      showServiceProductDialog: false,
      showTaxStampDialog: false
    }
  },

  computed: {
    ...mapGetters('RECEIPT', ['itemCount']),
    ...mapGetters('RECEIPT', { receiptProducts: 'products' }),
    ...mapState(['cashRegister'])
  },

  watch: {
    tag: {
      immediate: true,
      handler (tag) {
        this.updateResults()
      }
    }
  },

  created () {
    this.updateResults()
  },

  methods: {
    addToCart (item) {
      this.selectedProduct = item
      if (item.retailPriceTax > 0) {
        this.showTaxStampDialog = true
      } else {
        this.$wait.start(`${item.id} adding to cart`)
        return this.$store.dispatch('RECEIPT/ADD_PRODUCT', { id: item.id, qty: 1, returnType: this.returnType })
          .then(data => {
            item.receiptProduct = data.receiptProduct
            this.$emit('added-to-cart', item)
            this.showInfoDialog = false
            this.search = ''
            this.helpers.toastSuccess(data.message)
          })
          .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
          .finally(() => this.$wait.end(`${item.id} adding to cart`))
      }
    },
    fuzzy () {
      // Show all products when nothing searched
      if (this.search === '' || this.search === null) {
        this.filteredProducts = this.products
        return
      }

      this.filteredProducts = fuzzyfilterProducts(this.search, this.products)
    },
    fuzzyDebounced: _.debounce(function () {
      this.fuzzy()
    }, 300),
    openServiceProductDialog (item) {
      this.selectedProduct = item
      this.toggleServiceProductDialog()
    },
    saveServiceProductDialog (item) {
      this.selectedProduct = null
      this.$emit('added-to-cart', item)
      this.toggleServiceProductDialog()
    },
    scanCode (barcode) {
      this.search = barcode
      this.fuzzy()
    },
    selectTaxStamp (value) {
      this.$wait.start(`${this.selectedProduct.id} adding to cart`)
      return this.$store.dispatch('RECEIPT/ADD_PRODUCT', { id: this.selectedProduct.id, qty: 1, returnType: this.returnType, has_tax_stamp: value })
        .then(data => {
          this.selectedProduct.receiptProduct = data.receiptProduct
          this.helpers.toastSuccess(data.message)
          this.addedToCart(this.selectedProduct)
        })
        .catch(data => { if (data && data.message) this.helpers.toastError(data.message) })
        .finally(() => {
          this.$wait.end(`${this.selectedProduct.id} adding to cart`)
          this.showTaxStampDialog = false
          this.showInfoDialog = false
          this.search = ''
          this.$emit('added-to-cart', this.selectedProduct)
        })
    },
    showInfo (item) {
      if (this.itemIsProduct(item)) {
        this.selectedProduct = item
        this.showInfoDialog = true
      }

      if (this.itemIsService(item)) {
        this.openServiceProductDialog(item)
      }
    },
    showPurchases (item) {
      this.selectedProduct = item
      this.showProductReceiptsDialog = true
      this.showInfoDialog = false
    },
    toggleServiceProductDialog () {
      this.showServiceProductDialog = !this.showServiceProductDialog
    },
    updateResults () {
      this.showInfoDialog = false
      this.$wait.start('fetching products')

      // If component has a tag prop, find only the products with this tag
      if (this.tag) {
        return productService.findByTag(this.tag)
          .then((data) => {
            this.products = this.filteredProducts = camelcaseKeys(data.data.products, { deep: true })
          })
          .catch((data) => { if (data && data.data.message) Promise.reject(data.data.message) })
          .finally(() => this.$wait.end('fetching products'))
      }

      // ELSE
      // update the datatable items when store state has changed (async call in App.vue created)
      this.$store.watch(
        (state) => state.products,
        (products) => {
          this.products = this.filteredProducts = products
          if (products.length) {
            this.$wait.end('fetching products')
          }
          this.fuzzy()
        },
        { immediate: true }
      )
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/products/ProductsTable.vue